import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import useLocalStorage from "../hooks/useLocalStorage";

const SIDEBAR_STATE_KEY = "sidebar";

// Only these fields persist across reloads.
const DEFAULT_PERSISTED = {
  collapsedSections: {}, // { [sectionName]: true } — true = collapsed
  archiveOpen: false,
  desktopCollapsed: false, // full sidebar shrunk to icon-only rail on desktop
};

const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
  const [persisted, setPersisted] = useLocalStorage(
    SIDEBAR_STATE_KEY,
    DEFAULT_PERSISTED
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const state = { ...DEFAULT_PERSISTED, ...(persisted || {}) };

  const toggleSection = useCallback(
    (name) =>
      setPersisted((prev) => {
        const base = { ...DEFAULT_PERSISTED, ...(prev || {}) };
        return {
          ...base,
          collapsedSections: {
            ...base.collapsedSections,
            [name]: !base.collapsedSections[name],
          },
        };
      }),
    [setPersisted]
  );

  const isSectionCollapsed = useCallback(
    (name) => Boolean(state.collapsedSections[name]),
    [state.collapsedSections]
  );

  const setArchiveOpen = useCallback(
    (value) =>
      setPersisted((prev) => {
        const base = { ...DEFAULT_PERSISTED, ...(prev || {}) };
        return {
          ...base,
          archiveOpen:
            typeof value === "function" ? value(base.archiveOpen) : value,
        };
      }),
    [setPersisted]
  );

  const closeSidebar = useCallback(() => setMobileOpen(false), []);

  const toggleDesktopCollapsed = useCallback(
    () =>
      setPersisted((prev) => {
        const base = { ...DEFAULT_PERSISTED, ...(prev || {}) };
        return { ...base, desktopCollapsed: !base.desktopCollapsed };
      }),
    [setPersisted]
  );

  const resetSidebar = useCallback(
    () => setPersisted(DEFAULT_PERSISTED),
    [setPersisted]
  );

  const value = useMemo(
    () => ({
      collapsedSections: state.collapsedSections,
      archiveOpen: state.archiveOpen,
      desktopCollapsed: state.desktopCollapsed,
      mobileOpen,
      toggleSection,
      isSectionCollapsed,
      setArchiveOpen,
      setMobileOpen,
      closeSidebar,
      toggleDesktopCollapsed,
      resetSidebar,
    }),
    [
      state.collapsedSections,
      state.archiveOpen,
      state.desktopCollapsed,
      mobileOpen,
      toggleSection,
      isSectionCollapsed,
      setArchiveOpen,
      closeSidebar,
      toggleDesktopCollapsed,
      resetSidebar,
    ]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return ctx;
};
